import { useState, useEffect } from "react";
import {
  uploadBankStatements,
  getBankNames,
  getAccountNumbers,
  getFileTypes,
} from "../Api/BankStatements";
import { Container, Form, Button, Row, Col } from "react-bootstrap";
import { getAllHouseBanks } from "../Api/HouseBank";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Sample14 from "../Components/Logo";
import { useAuth } from "../Context/AuthContext";
const UploadBankStatements = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [houseBanksLoading, setHouseBanksLoading] = useState(false);
  const{user}=useAuth();
  // Get user details from localStorage
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId;

  // State for dropdown options
  const [bankNames, setBankNames] = useState([]);
  const [accountNumbers, setAccountNumbers] = useState([]);
  const [fileTypes, setFileTypes] = useState([]);
  const [houseBanks, setHouseBanks] = useState([]);
  const [filteredHouseBanks, setFilteredHouseBanks] = useState([]);

  const [formData, setFormData] = useState({
    bankName: "",
    accountNo: "",
    fileType: "",
    housebankId: "",
    file: null,
  });

  useEffect(() => {
    const fetchDropdownData = async () => {
      setDropdownLoading(true);
      try {
        const [bankNamesData, fileTypesData, houseBanksData] =
          await Promise.all([
            getBankNames(user?.organisationId),
            getFileTypes(),
            getAllHouseBanks(user?.organisationId),
          ]);
        setBankNames(bankNamesData);
        setFileTypes(fileTypesData);
        setHouseBanks(houseBanksData);
      } catch (error) {
        toast.error("Error loading dropdown data");
      } finally {
        setDropdownLoading(false);
      }
    };

    fetchDropdownData();
  }, []);

  useEffect(() => {
    // Filter house banks based on selected bank name
    if (formData.bankName && houseBanks.length > 0) {
      const filtered = houseBanks.filter(
        (bank) =>
          bank.bankName.toLowerCase() === formData.bankName.toLowerCase()
      );
      setFilteredHouseBanks(filtered);
      console.log(filtered);

      // Auto-select house bank if only one matches
      if (filtered.length === 1) {
        setFormData((prev) => ({
          ...prev,
          housebankId: filtered[0].housebankId, // match API property
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          housebankId: "",
        }));
      }
    } else {
      setFilteredHouseBanks([]);
      setFormData((prev) => ({
        ...prev,
        housebankId: "",
      }));
    }
  }, [formData.bankName, houseBanks]);

  useEffect(() => {
    const fetchAccountNumbers = async () => {
      if (formData.bankName && userId) {
        setAccountLoading(true);
        try {
          const accounts = await getAccountNumbers(formData.bankName, userId,user?.organisationId);
          setAccountNumbers(accounts);
          setFormData((prev) => ({ ...prev, accountNo: "" }));
        } catch (error) {
          toast.error(`Error loading account numbers for ${formData.bankName}`);
          setAccountNumbers([]);
        } finally {
          setAccountLoading(false);
        }
      } else {
        setAccountNumbers([]);
      }
    };

    fetchAccountNumbers();
  }, [formData.bankName, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, file: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.file) {
      toast.error("Please select a file to upload");
      return;
    }

    if (
      !formData.bankName ||
      !formData.accountNo ||
      !formData.fileType ||
      !formData.housebankId
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    if (!userId) {
      toast.error("User information not found. Please login again.");
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();
      data.append("CSVFile", formData.file);
      data.append("BankName", formData.bankName);
      data.append("AccountNumber", formData.accountNo);
      data.append("FileType", formData.fileType);
      data.append("housebankId", formData.housebankId);

      await uploadBankStatements(data, userId);
      toast.success("🦄 Bank Statements Uploaded Successfully");
      setFormData({
        bankName: "",
        accountNo: "",
        fileType: "",
        housebankId: "",
        file: null,
      });
    } catch (error) {
      console.log("Error:", error);
      toast.error(error.message || "🦄 Error Uploading Bank Statements");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="main-content expand">
        <Container fluid className="fields-container">
          <h2 className="fields-title">Upload Bank Statements</h2>

          <Form onSubmit={handleSubmit} className="fields-form">
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Bank Name <span className="text-red">**</span>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleChange}
                    required
                    disabled={dropdownLoading}
                  >
                    <option value="">Select Bank</option>
                    {bankNames.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Account No <span className="text-red">**</span>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="accountNo"
                    value={formData.accountNo}
                    onChange={handleChange}
                    required
                    disabled={!formData.bankName || accountLoading}
                  >
                    <option value="">Select Account</option>
                    {accountNumbers.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    File Type <span className="text-red">**</span>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="fileType"
                    value={formData.fileType}
                    onChange={handleChange}
                    required
                    disabled={dropdownLoading}
                  >
                    <option value="">Select File Type</option>
                    {fileTypes.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    House Bank <span className="text-red">**</span>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="housebankId"
                    value={formData.housebankId}
                    onChange={handleChange}
                    required
                    disabled={!formData.bankName || dropdownLoading}
                  >
                    <option value="">Select House Bank</option>
                    {filteredHouseBanks.map((bank) => (
                      <option key={bank.housebankId} value={bank.housebankId}>
                        {bank.bankName} - {bank.housebankId}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Upload File <span className="text-red">**</span>
                  </Form.Label>
                  <Form.Control
                    type="file"
                    name="file"
                    onChange={handleFileChange}
                    required
                    accept=".csv,.xlsx,.pdf"
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="fields-btn-container">
              <Button
                variant="primary"
                type="submit"
                className="fields-submit"
                disabled={loading}
              >
                {loading ? "Uploading..." : "Upload Statements"}
              </Button>
            </div>
          </Form>
        </Container>
      </div>

      {(loading || dropdownLoading || accountLoading || houseBanksLoading) && (
        <div className="loading-overlay">
          <Sample14 />
        </div>
      )}
    </>
  );
};

export default UploadBankStatements;

// import { useState, useEffect } from "react";
// import {
//   uploadBankStatements,
//   getBankNames,
//   getAccountNumbers,
//   getFileTypes,
// } from "../Api/BankStatements";
// import { Container, Form, Button, Row, Col } from "react-bootstrap";
// import { getAllHouseBanks } from "../Api/HouseBank";
// import { toast } from "react-toastify";
// import { useNavigate } from "react-router-dom";
// import Sample14 from "../Components/Logo";

// const UploadBankStatements = () => {
//   const navigate = useNavigate();
//   const [loading, setLoading] = useState(false);
//   const [dropdownLoading, setDropdownLoading] = useState(false);
//   const [accountLoading, setAccountLoading] = useState(false);

//   // Get user details from localStorage
//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const userId = userDetails?.userId;

//   // State for dropdown options
//   const [bankNames, setBankNames] = useState([]);
//   const [accountNumbers, setAccountNumbers] = useState([]);
//   const [fileTypes, setFileTypes] = useState([]);

//   const [formData, setFormData] = useState({
//     bankName: "",
//     accountNo: "",
//     fileType: "",
//     file: null,
//   });

//   useEffect(() => {
//     const fetchDropdownData = async () => {
//       setDropdownLoading(true);
//       try {
//         const [bankNamesData, fileTypesData] = await Promise.all([
//           getBankNames(),
//           getFileTypes(),
//         ]);
//         setBankNames(bankNamesData);
//         setFileTypes(fileTypesData);
//       } catch (error) {
//         toast.error("Error loading dropdown data");
//       } finally {
//         setDropdownLoading(false);
//       }
//     };

//     fetchDropdownData();
//   }, []);

//   useEffect(() => {
//     const fetchAccountNumbers = async () => {
//       if (formData.bankName && userId) {
//         setAccountLoading(true);
//         try {
//           const accounts = await getAccountNumbers(formData.bankName, userId);
//           setAccountNumbers(accounts);
//           setFormData((prev) => ({ ...prev, accountNo: "" }));
//         } catch (error) {
//           toast.error(`Error loading account numbers for ${formData.bankName}`);
//           setAccountNumbers([]);
//         } finally {
//           setAccountLoading(false);
//         }
//       } else {
//         setAccountNumbers([]);
//       }
//     };

//     fetchAccountNumbers();
//   }, [formData.bankName, userId]);

//   const handleChange = (e) => {
//     setFormData({ ...formData, [e.target.name]: e.target.value });
//   };

//   const handleFileChange = (e) => {
//     setFormData({ ...formData, file: e.target.files[0] });
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!formData.file) {
//       toast.error("Please select a file to upload");
//       return;
//     }

//     if (!formData.bankName || !formData.accountNo || !formData.fileType) {
//       toast.error("Please fill all required fields");
//       return;
//     }

//     if (!userId) {
//       toast.error("User information not found. Please login again.");
//       return;
//     }

//     setLoading(true);

//     try {
//       const data = new FormData();
//       data.append("CSVFile", formData.file); // Changed from ExcelFile to CSVFile
//       data.append("BankName", formData.bankName);
//       data.append("AccountNumber", formData.accountNo);
//       data.append("FileType", formData.fileType);

//       await uploadBankStatements(data, userId);
//       toast.success("🦄 Bank Statements Uploaded Successfully");
//       setFormData({
//         bankName: "",
//         accountNo: "",
//         fileType: "",
//         file: null,
//       });
//     } catch (error) {
//       console.log("Error:", error);
//       toast.error(error.message || "🦄 Error Uploading Bank Statements");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <>
//       <div className="main-content expand">
//         <Container fluid className="fields-container">
//           <h2 className="fields-title">Upload Bank Statements</h2>

//           <Form onSubmit={handleSubmit} className="fields-form">
//             <Row>
//               <Col md={4}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>
//                     Bank Name <span className="text-red">**</span>
//                   </Form.Label>
//                   <Form.Control
//                     as="select"
//                     name="bankName"
//                     value={formData.bankName}
//                     onChange={handleChange}
//                     required
//                     disabled={dropdownLoading}
//                   >
//                     <option value="">Select Bank</option>
//                     {bankNames.map((option) => (
//                       <option key={option.value} value={option.value}>
//                         {option.label}
//                       </option>
//                     ))}
//                   </Form.Control>
//                 </Form.Group>
//               </Col>
//               <Col md={4}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>
//                     Account No <span className="text-red">**</span>
//                   </Form.Label>
//                   <Form.Control
//                     as="select"
//                     name="accountNo"
//                     value={formData.accountNo}
//                     onChange={handleChange}
//                     required
//                     disabled={!formData.bankName || accountLoading}
//                   >
//                     <option value="">Select Account</option>
//                     {accountNumbers.map((option) => (
//                       <option key={option.value} value={option.value}>
//                         {option.label}
//                       </option>
//                     ))}
//                   </Form.Control>
//                 </Form.Group>
//               </Col>
//               <Col md={4}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>
//                     File Type <span className="text-red">**</span>
//                   </Form.Label>
//                   <Form.Control
//                     as="select"
//                     name="fileType"
//                     value={formData.fileType}
//                     onChange={handleChange}
//                     required
//                     disabled={dropdownLoading}
//                   >
//                     <option value="">Select File Type</option>
//                     {fileTypes.map((option) => (
//                       <option key={option.value} value={option.value}>
//                         {option.label}
//                       </option>
//                     ))}
//                   </Form.Control>
//                 </Form.Group>
//               </Col>
//             </Row>

//             <Row>
//               <Col md={6}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>
//                     Upload File <span className="text-red">**</span>
//                   </Form.Label>
//                   <Form.Control
//                     type="file"
//                     name="file"
//                     onChange={handleFileChange}
//                     required
//                     accept=".csv,.xlsx,.pdf" // Added accept attribute for better UX
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>

//             <div className="fields-btn-container">
//               <Button
//                 variant="primary"
//                 type="submit"
//                 className="fields-submit"
//                 disabled={loading}
//               >
//                 {loading ? "Uploading..." : "Upload Statements"}
//               </Button>
//             </div>
//           </Form>
//         </Container>
//       </div>

//       {(loading || dropdownLoading || accountLoading) && (
//         <div className="loading-overlay">
//           <Sample14 />
//         </div>
//       )}
//     </>
//   );
// };

// export default UploadBankStatements;
